<template>
  <div
    class="min-h-full bg-transparent text-slate-800 dark:text-[#F1F5F9] pb-16 transition-colors duration-200"
  >
    <div class="max-w-5xl mx-auto space-y-6 sm:space-y-8 pb-12">
      <!-- ======================================================== -->
      <!-- 1. PREMIUM MINIMAL HERO HEADER                           -->
      <!-- ======================================================== -->
      <div class="text-center pt-2 sm:pt-4 max-w-2xl mx-auto">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/50 border border-blue-200/70 dark:border-blue-800/40 text-[11px] font-bold text-blue-700 dark:text-blue-300 mb-3 shadow-2xs"
        >
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span>EduManage Support Center</span>
          <span class="opacity-40">•</span>
          <span>Mon – Sat, 9:00 AM – 7:00 PM IST</span>
        </div>

        <h1
          class="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight"
        >
          How can we help your school?
        </h1>

        <p
          class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-2 leading-relaxed"
        >
          Get fast, expert assistance with RFID attendance, website studio,
          student records, and fee management.
        </p>
      </div>

      <!-- ======================================================== -->
      <!-- 2. THREE PRIMARY CONTACT CARDS (CLEAN & ATTRACTIVE)      -->
      <!-- ======================================================== -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        <!-- PHONE CARD -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-blue-400/60 dark:hover:border-blue-600/60 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="flex items-center justify-between mb-4">
              <div
                class="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center border border-blue-100 dark:border-blue-900/40 group-hover:scale-105 transition-transform"
              >
                <PhoneCall class="w-5 h-5" />
              </div>
              <span
                class="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 px-2 py-0.5 rounded-md"
              >
                Direct Call
              </span>
            </div>

            <h3 class="text-base font-bold text-slate-900 dark:text-white">
              Phone Support
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5 mb-4">
              Speak directly with an engineer for immediate assistance.
            </p>

            <!-- Phone Display & Copy -->
            <div
              class="p-3 bg-slate-50 dark:bg-[#18243A] rounded-xl border border-slate-100 dark:border-[#263449] flex items-center justify-between mb-4"
            >
              <span
                class="text-sm font-mono font-bold text-slate-900 dark:text-white select-all"
              >
                +91 {{ supportPhone }}
              </span>
              <button
                type="button"
                @click="copyText(supportPhone)"
                class="p-1 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                title="Copy Number"
              >
                <Check
                  v-if="copiedPhone"
                  class="w-4 h-4 text-emerald-500"
                />
                <Copy
                  v-else
                  class="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <a
            :href="`tel:${supportPhone}`"
            class="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-sm shadow-blue-500/20 active:scale-95 transition cursor-pointer"
          >
            <Phone class="w-3.5 h-3.5" />
            <span>Call +91 {{ supportPhone }}</span>
          </a>
        </div>

        <!-- WHATSAPP CARD -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-emerald-400/60 dark:hover:border-emerald-600/60 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="flex items-center justify-between mb-4">
              <div
                class="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-100 dark:border-emerald-900/40 group-hover:scale-105 transition-transform"
              >
                <MessageSquareMore class="w-5 h-5" />
              </div>
              <span
                class="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-md"
              >
                Fast Chat
              </span>
            </div>

            <h3 class="text-base font-bold text-slate-900 dark:text-white">
              WhatsApp Desk
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5 mb-4">
              Send screenshots and chat with our team in real time.
            </p>

            <!-- Number Display & Copy -->
            <div
              class="p-3 bg-slate-50 dark:bg-[#18243A] rounded-xl border border-slate-100 dark:border-[#263449] flex items-center justify-between mb-4"
            >
              <span
                class="text-sm font-mono font-bold text-slate-900 dark:text-white select-all"
              >
                +91 {{ supportPhone }}
              </span>
              <button
                type="button"
                @click="copyText(supportPhone)"
                class="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition"
                title="Copy Number"
              >
                <Check
                  v-if="copiedPhone"
                  class="w-4 h-4 text-emerald-500"
                />
                <Copy
                  v-else
                  class="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <a
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm shadow-emerald-500/20 active:scale-95 transition cursor-pointer"
          >
            <MessageCircle class="w-3.5 h-3.5" />
            <span>Chat on WhatsApp</span>
            <ExternalLink class="w-3 h-3 opacity-70" />
          </a>
        </div>

        <!-- EMAIL CARD -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-5 sm:p-6 shadow-sm hover:shadow-md hover:border-indigo-400/60 dark:hover:border-indigo-600/60 transition-all flex flex-col justify-between group"
        >
          <div>
            <div class="flex items-center justify-between mb-4">
              <div
                class="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-100 dark:border-indigo-900/40 group-hover:scale-105 transition-transform"
              >
                <Mail class="w-5 h-5" />
              </div>
              <span
                class="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-md"
              >
                Official Desk
              </span>
            </div>

            <h3 class="text-base font-bold text-slate-900 dark:text-white">
              Email Support
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5 mb-4">
              For formal requests, custom domains, or billing questions.
            </p>

            <!-- Email Display & Copy -->
            <div
              class="p-3 bg-slate-50 dark:bg-[#18243A] rounded-xl border border-slate-100 dark:border-[#263449] flex items-center justify-between mb-4"
            >
              <span
                class="text-xs font-mono font-bold text-slate-900 dark:text-white truncate select-all"
              >
                {{ supportEmail }}
              </span>
              <button
                type="button"
                @click="copyText(supportEmail)"
                class="p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition shrink-0"
                title="Copy Email"
              >
                <Check
                  v-if="copiedEmail"
                  class="w-4 h-4 text-emerald-500"
                />
                <Copy
                  v-else
                  class="w-4 h-4"
                />
              </button>
            </div>
          </div>

          <a
            :href="`mailto:${supportEmail}?subject=EduManage%20Support%20Request`"
            class="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm shadow-indigo-500/20 active:scale-95 transition cursor-pointer"
          >
            <Send class="w-3.5 h-3.5" />
            <span>Send Email</span>
          </a>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- 3. SIMPLE, ATTRACTIVE QUICK INQUIRY FORM                 -->
      <!-- ======================================================== -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-5 sm:p-7 shadow-sm"
      >
        <div
          class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4 mb-5"
        >
          <div class="flex items-center gap-2.5">
            <div
              class="p-2 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-xl"
            >
              <Send class="w-4 h-4" />
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-900 dark:text-white">
                Send a Quick Message
              </h3>
              <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                Type your question below and dispatch via WhatsApp or Email
                instantly.
              </p>
            </div>
          </div>

          <span class="text-[11px] font-bold text-slate-400 hidden sm:inline">
            Direct response to {{ supportEmail }}
          </span>
        </div>

        <!-- Topic Pills Selector -->
        <div class="mb-4">
          <label
            class="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2"
          >
            Select Topic:
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="cat in topicOptions"
              :key="cat.id"
              type="button"
              @click="selectedTopic = cat.id"
              :class="[
                'px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer border',
                selectedTopic === cat.id
                  ? 'bg-blue-600 text-white border-blue-600 shadow-2xs'
                  : 'bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] border-slate-200/80 dark:border-[#263449] hover:bg-slate-100 dark:hover:bg-[#1F2E47]',
              ]"
            >
              {{ cat.label }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <!-- School & Name -->
          <div>
            <label
              class="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5"
            >
              School Name
            </label>
            <input
              v-model="userSchool"
              type="text"
              placeholder="e.g. Cambridge Academy"
              class="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
          </div>

          <!-- Contact Phone or Email -->
          <div>
            <label
              class="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5"
            >
              Your Phone / Email
            </label>
            <input
              v-model="userContact"
              type="text"
              placeholder="e.g. 9876543210 or admin@school.com"
              class="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
          </div>
        </div>

        <!-- Message Box -->
        <div class="mb-5">
          <label
            class="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5"
          >
            How can we help?
          </label>
          <textarea
            v-model="userMessage"
            rows="3"
            placeholder="Briefly describe what you need assistance with..."
            class="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition custom-scrollbar"
          />
        </div>

        <!-- Submission Buttons -->
        <div
          class="flex flex-col sm:flex-row items-center justify-end gap-3 pt-1"
        >
          <button
            type="button"
            @click="sendWhatsAppMessage"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs active:scale-95 transition cursor-pointer"
          >
            <MessageCircle class="w-4 h-4" />
            <span>Send via WhatsApp (+91 {{ supportPhone }})</span>
          </button>

          <button
            type="button"
            @click="sendEmailMessage"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs active:scale-95 transition cursor-pointer"
          >
            <Mail class="w-4 h-4" />
            <span>Send to {{ supportEmail }}</span>
          </button>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- 4. CURATED COMMON FAQS (CLEAN ACCORDION)                 -->
      <!-- ======================================================== -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-5 sm:p-7 shadow-sm"
      >
        <div
          class="flex items-center gap-2.5 mb-5 border-b border-slate-100 dark:border-[#263449] pb-4"
        >
          <div
            class="p-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl"
          >
            <HelpCircle class="w-4 h-4" />
          </div>
          <div>
            <h3 class="text-base font-bold text-slate-900 dark:text-white">
              Frequently Asked Questions
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
              Quick answers to the most common questions from school
              administrators.
            </p>
          </div>
        </div>

        <div class="space-y-3">
          <div
            v-for="(faq, idx) in commonFaqs"
            :key="idx"
            class="rounded-xl border border-slate-200/80 dark:border-[#263449] overflow-hidden transition-all"
            :class="
              openFaq === idx
                ? 'bg-slate-50/60 dark:bg-[#18243A]/40 ring-1 ring-blue-500/20'
                : 'bg-white dark:bg-[#111C30]'
            "
          >
            <button
              type="button"
              @click="openFaq = openFaq === idx ? null : idx"
              class="w-full p-4 flex items-center justify-between gap-4 text-left font-bold text-xs sm:text-sm text-slate-900 dark:text-white transition cursor-pointer select-none"
            >
              <div class="flex items-center gap-2.5">
                <span
                  class="w-5 h-5 rounded-md bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-black shrink-0"
                >
                  Q
                </span>
                <span>{{ faq.question }}</span>
              </div>
              <ChevronDown
                :class="[
                  'w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0',
                  openFaq === idx
                    ? 'rotate-180 text-blue-600 dark:text-blue-400'
                    : '',
                ]"
              />
            </button>

            <div
              v-show="openFaq === idx"
              class="px-4 pb-4 pt-1 text-xs text-slate-600 dark:text-[#A6B4C8] leading-relaxed border-t border-slate-100 dark:border-[#263449]/60"
            >
              <div class="mt-2 pl-7">
                <p>{{ faq.answer }}</p>
                <div
                  v-if="faq.link"
                  class="mt-2"
                >
                  <router-link
                    :to="faq.link"
                    class="inline-flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400 hover:underline text-xs"
                  >
                    <span>{{ faq.linkLabel }}</span>
                    <ArrowRight class="w-3 h-3" />
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import {
    PhoneCall,
    Phone,
    Mail,
    Send,
    MessageCircle,
    MessageSquareMore,
    Copy,
    Check,
    HelpCircle,
    ChevronDown,
    ArrowRight,
    ExternalLink,
  } from "lucide-vue-next";

  const store = useStore();

  /* =========================================
   SUPPORT CREDENTIALS (FROM USER REQUIREMENT)
  ========================================= */
  const supportPhone = "8638803228";
  const supportEmail = "support@educanium.com";

  const copiedPhone = ref(false);
  const copiedEmail = ref(false);
  const openFaq = ref(0);

  const selectedTopic = ref("rfid");
  const userSchool = ref("");
  const userContact = ref("");
  const userMessage = ref("");

  const topicOptions = [
    { id: "rfid", label: "RFID & Hardware" },
    { id: "website", label: "Public Website Studio" },
    { id: "students", label: "Students & Promotion" },
    { id: "fees", label: "Fee Collections" },
    { id: "other", label: "General Support" },
  ];

  onMounted(async () => {
    try {
      if (!store.state.schools?.length) {
        await store.dispatch("fetchMySchool");
      }
      const school = store.state.schools?.[0] || store.state.mySchool || {};
      if (school.schoolName) userSchool.value = school.schoolName;
      if (school.primaryPhone) userContact.value = school.primaryPhone;
      else if (school.email) userContact.value = school.email;
    } catch {
      // Fallback cleanly
    }
  });

  const whatsappUrl = computed(() => {
    const text = encodeURIComponent(
      `Hello EduManage Support Team, I am reaching out from ${
        userSchool.value || "my school"
      } regarding technical support.`,
    );
    return `https://wa.me/91${supportPhone}?text=${text}`;
  });

  const copyText = (text) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(text);
    }
    if (text === supportPhone) {
      copiedPhone.value = true;
      setTimeout(() => (copiedPhone.value = false), 2000);
    } else {
      copiedEmail.value = true;
      setTimeout(() => (copiedEmail.value = false), 2000);
    }
  };

  const sendWhatsAppMessage = () => {
    const msg = encodeURIComponent(
      `*EduManage Support Inquiry*\n*School:* ${
        userSchool.value || "N/A"
      }\n*Contact:* ${userContact.value || "N/A"}\n*Topic:* ${
        selectedTopic.value
      }\n*Message:* ${userMessage.value || "I need assistance."}`,
    );
    window.open(`https://wa.me/91${supportPhone}?text=${msg}`, "_blank");
  };

  const sendEmailMessage = () => {
    const subject = encodeURIComponent(
      `EduManage Support: [${selectedTopic.value.toUpperCase()}] ${
        userSchool.value || ""
      }`,
    );
    const body = encodeURIComponent(
      `School Name: ${userSchool.value || ""}\nContact: ${
        userContact.value || ""
      }\nTopic: ${selectedTopic.value}\n\nMessage:\n${userMessage.value || ""}`,
    );
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const commonFaqs = [
    {
      question: "How do I pair or register a new RFID biometric terminal?",
      answer:
        "Go to 'Smart Cards & Devices' from the sidebar menu, click 'Add New Device', enter the reader's device name, room, and MAC ID. Attendance swipes will stream to your portal in real time.",
      link: "/devices",
      linkLabel: "Open Devices Manager",
    },
    {
      question: "How do I launch or customize our school's public website?",
      answer:
        "Open 'WEB Dashboard' from the sidebar. You can choose a theme, customize the hero banner, programs, campus gallery, and leadership message. Once saved, your site is immediately live.",
      link: "/WEBDashboard",
      linkLabel: "Open Web Dashboard",
    },
    {
      question:
        "How do I promote an entire class batch to the next academic session?",
      answer:
        "Navigate to 'Students Directory', choose your class from the dropdown, click 'Bulk Actions' > 'Promote Class', select the destination grade, and confirm.",
      link: "/students",
      linkLabel: "Go to Students Directory",
    },
    {
      question:
        "How do I collect and record offline cash or cheque fee payments?",
      answer:
        "In 'Fee Management', find the student, click 'Record Payment', select Cash, Cheque, or UPI, input the receipt number, and click 'Confirm Payment' to issue an instant receipt.",
      link: "/fee-management",
      linkLabel: "Go to Fee Management",
    },
    {
      question:
        "Where can I view advanced hardware diagnostics and terminal logs?",
      answer:
        "Open 'Device Operations' from the sidebar to inspect multi-school terminal status, gateway connectivity, and smart card allocations.",
      link: "/device-operations",
      linkLabel: "Open Device Operations",
    },
  ];
</script>
